// src/pages/AdminProvidersPage.jsx — Bloque 4
import { useState, useMemo, useEffect, useCallback } from "react";
import api from "../api/api";

const font = "Montserrat, sans-serif";
const inputStyle = { padding: "9px 12px", borderRadius: 8, border: "1.5px solid #cddae4", fontSize: 13, fontFamily: font, color: "#163244", background: "#fff", outline: "none", boxSizing: "border-box" };
const PAGE_SIZE  = 25;
const MONTHS_ES  = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

const STATUS_CONFIG = {
  active:         { label: "Activo",       color: "#1b7f4e", bg: "#e8f7ef" },
  pending_review: { label: "En revisión",  color: "#b06a00", bg: "#fff3e0" },
  draft:          { label: "Borrador",     color: "#5c7a8a", bg: "#f0f5f8" },
  suspended:      { label: "Suspendido",   color: "#c0392b", bg: "#fdecea" },
  rejected:       { label: "Rechazado",    color: "#c0392b", bg: "#fdecea" },
  approved:       { label: "Aprobado",     color: "#1b7f4e", bg: "#e8f7ef" },
  pending:        { label: "Pendiente",    color: "#b06a00", bg: "#fff3e0" },
  expired:        { label: "Vencido",      color: "#7b5ea7", bg: "#f3eeff" },
};

const PROFILE_STATUS_ACTIONS = {
  active:         [{ action: "suspended", label: "Suspender", color: "#c0392b" }, { action: "rejected", label: "Rechazar", color: "#7b5ea7" }],
  pending_review: [{ action: "active",    label: "Aprobar",   color: "#1b7f4e" }, { action: "rejected", label: "Rechazar", color: "#c0392b" }],
  draft:          [{ action: "active",    label: "Activar",   color: "#1b7f4e" }],
  suspended:      [{ action: "active",    label: "Reactivar", color: "#1b7f4e" }, { action: "rejected", label: "Rechazar", color: "#c0392b" }],
  rejected:       [{ action: "active",    label: "Reactivar", color: "#1b7f4e" }],
};

const DOC_STATUS_ACTIONS = {
  null:     [{ action: "approved", label: "Aprobar",  color: "#1b7f4e" }, { action: "rejected", label: "Rechazar", color: "#c0392b" }],
  pending:  [{ action: "approved", label: "Aprobar",  color: "#1b7f4e" }, { action: "rejected", label: "Rechazar", color: "#c0392b" }],
  approved: [{ action: "rejected", label: "Rechazar", color: "#c0392b" }],
  rejected: [{ action: "approved", label: "Aprobar",  color: "#1b7f4e" }],
};

function Badge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#5c7a8a", bg: "#f0f5f8" };
  return <span style={{ padding: "2px 9px", borderRadius: 20, fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", color: cfg.color, background: cfg.bg, display: "inline-block", whiteSpace: "nowrap" }}>{cfg.label.toUpperCase()}</span>;
}

function ActionBtn({ label, color, onClick, loading }) {
  return (
    <button onClick={onClick} disabled={loading} style={{ padding: "5px 12px", borderRadius: 6, border: `1.5px solid ${color}`, background: "transparent", color, cursor: loading ? "not-allowed" : "pointer", fontFamily: font, fontWeight: 600, fontSize: 11, transition: "all 0.15s", opacity: loading ? 0.6 : 1 }}
      onMouseEnter={e => { if (!loading) { e.target.style.background = color; e.target.style.color = "#fff"; }}}
      onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = color; }}
    >{loading ? "..." : label}</button>
  );
}

// ─── Drawer (idéntico al original, sin cambios funcionales) ───────────────────
function ProviderDrawer({ provider, onClose, onUpdate }) {
  const [loadingProfile, setLoadingProfile] = useState(null);
  const [loadingDoc,     setLoadingDoc]     = useState(null);
  const [localProvider,  setLocalProvider]  = useState({ documents: [], services: [], ...provider });
  const [rejectModal,    setRejectModal]    = useState(null);
  const [rejectReason,   setRejectReason]   = useState("");

  useEffect(() => {
    if (provider && (provider.services?.length > 0 || provider.documents?.length > 0)) {
      setLocalProvider({ documents: [], services: [], ...provider });
    }
  }, [provider]);

  const handleProfileStatus = async (newStatus) => {
    if (newStatus === "rejected") { setRejectModal({ type: "profile" }); return; }
    setLoadingProfile(newStatus);
    try {
      await api.patch(`/providers/admin-op/${localProvider.id}/profile-status`, { status: newStatus });
      const updated = { ...localProvider, status: newStatus };
      setLocalProvider(updated); onUpdate(updated);
    } catch (err) { console.error(err); }
    finally { setLoadingProfile(null); }
  };

  const handleServiceStatus = async (serviceId, newStatus) => {
    try {
      await api.patch(`/providers/admin-op/${localProvider.id}/services/${serviceId}/status`, { status: newStatus });
      const updated = { ...localProvider, services: localProvider.services.map(s => s.id === serviceId ? { ...s, status: newStatus } : s) };
      setLocalProvider(updated); onUpdate(updated);
    } catch (err) { console.error(err); }
  };

  const handleDocStatus = async (docId, newStatus) => {
    if (newStatus === "rejected") { setRejectModal({ type: "doc", docId }); return; }
    setLoadingDoc(docId + newStatus);
    try {
      // El backend lee verification_status: con { status } respondía 400
      // "Estado inválido" y aprobar documentos desde el panel nunca funcionó.
      const res = await api.patch(`/providers/admin-op/${localProvider.id}/documents/${docId}/status`, { verification_status: newStatus });
      const updatedDocs = localProvider.documents.map(d => d.id === docId ? { ...d, verification_status: newStatus, status: newStatus } : d);
      const updatedServices = res.data.updated_service
        ? localProvider.services.map(s => s.id === res.data.updated_service.id ? { ...s, ...res.data.updated_service } : s)
        : localProvider.services;
      const updated = { ...localProvider, documents: updatedDocs, services: updatedServices };
      setLocalProvider(updated); onUpdate(updated);
    } catch (err) { console.error(err); }
    finally { setLoadingDoc(null); }
  };

  const confirmReject = async () => {
    const { type, docId } = rejectModal;
    setRejectModal(null);
    if (type === "profile") {
      setLoadingProfile("rejected");
      try {
        await api.patch(`/providers/admin-op/${localProvider.id}/profile-status`, { status: "rejected", reason: rejectReason });
        const updated = { ...localProvider, status: "rejected" };
        setLocalProvider(updated); onUpdate(updated);
      } catch (err) { console.error(err); }
      finally { setLoadingProfile(null); }
    } else {
      setLoadingDoc(docId + "rejected");
      try {
        await api.patch(`/providers/admin-op/${localProvider.id}/documents/${docId}/status`, { verification_status: "rejected", rejection_reason: rejectReason });
        const updatedDocs = localProvider.documents.map(d => d.id === docId ? { ...d, status: "rejected", rejection_reason: rejectReason } : d);
        const updated = { ...localProvider, documents: updatedDocs };
        setLocalProvider(updated); onUpdate(updated);
      } catch (err) { console.error(err); }
      finally { setLoadingDoc(null); }
    }
    setRejectReason("");
  };

  const p = localProvider;
  const profileActions = PROFILE_STATUS_ACTIONS[p.status] || [];

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(22,50,68,0.45)", zIndex: 100, backdropFilter: "blur(2px)" }} />
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(560px, 95vw)", background: "#fff", zIndex: 101, overflowY: "auto", boxShadow: "-8px 0 40px rgba(22,50,68,0.18)", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ background: "#163244", padding: "18px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 90% 20%, rgba(231,164,50,0.15) 0%, transparent 55%)", pointerEvents: "none" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 14, zIndex: 1 }}>
            {p.profile_photo_url
              ? <img src={p.profile_photo_url} alt="" style={{ width: 46, height: 46, borderRadius: "50%", objectFit: "cover", border: "2px solid #E7A432" }} />
              : <div style={{ width: 46, height: 46, borderRadius: "50%", background: "#E7A432", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18, color: "#163244", flexShrink: 0 }}>{p.display_name[0]}</div>
            }
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 15, marginBottom: 2 }}>{p.display_name}</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>{p.email} · DNI {p.dni}</div>
              <div style={{ display: "flex", gap: 6, marginTop: 5 }}>
                <Badge status={p.status} />
                {p.rating_avg && <span style={{ fontSize: 10, color: "#E7A432", fontWeight: 700 }}>⭐ {parseFloat(p.rating_avg).toFixed(1)}</span>}
                {p.jobs_completed > 0 && <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>{p.jobs_completed} trabajos</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 28, height: 28, borderRadius: "50%", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>✕</button>
        </div>
        <div style={{ background: "#E7A432", height: 3, flexShrink: 0 }} />

        <div style={{ padding: "20px 22px", flex: 1 }}>

          {/* Estado + acciones */}
          <div style={{ background: "#f7fafc", borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 10, color: "#8fa3b1", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>Estado del perfil</div>
              <Badge status={p.status} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {profileActions.map(a => <ActionBtn key={a.action} label={a.label} color={a.color} loading={loadingProfile === a.action} onClick={() => handleProfileStatus(a.action)} />)}
            </div>
          </div>

          {/* Verificaciones */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            {[{ label: "Identidad", ok: p.identity_verified }, { label: "Teléfono", ok: p.phone_verified }].map(v => (
              <div key={v.label} style={{ flex: 1, padding: "10px 14px", borderRadius: 10, background: v.ok ? "#e8f7ef" : "#fdecea", border: `1px solid ${v.ok ? "#1b7f4e" : "#e57373"}`, fontSize: 12, fontWeight: 600, color: v.ok ? "#1b7f4e" : "#c0392b", display: "flex", justifyContent: "space-between" }}>
                {v.label} verificado <span>{v.ok ? "✓" : "✕"}</span>
              </div>
            ))}
          </div>

          {/* Servicios */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Servicios ({(p.services || []).length})</div>
            {(p.services || []).map(s => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 8, background: "#f7fafc", border: "1px solid #e0e9ef", marginBottom: 6 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 13, color: "#163244" }}>{s.service_name || s.label}</span>
                  <span style={{ fontSize: 11, color: "#8fa3b1", marginLeft: 8 }}>Nivel {s.validation_level}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Badge status={s.status} />
                  {(s.status === "pending_documents" || s.status === "pending_review") && (
                    <button onClick={() => handleServiceStatus(s.id, "approved")} style={{ padding: "3px 10px", borderRadius: 6, border: "none", background: "#1b7f4e", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: font }}>Aprobar</button>
                  )}
                  {s.status === "approved" && (
                    <button onClick={() => handleServiceStatus(s.id, "rejected")} style={{ padding: "3px 10px", borderRadius: 6, border: "none", background: "#c0392b", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: font }}>Rechazar</button>
                  )}
                  {s.status === "expired" && (
                    // Reaprobar a mano un rubro vencido (lo venció expirationJob). El
                    // camino normal es que el prestador suba la documentación nueva y
                    // se apruebe; esto es para destrabarlo sin tocar la base.
                    <button onClick={() => { if (window.confirm("¿Reaprobar este rubro vencido? Va a volver a recibir solicitudes aunque su documentación siga vencida.")) handleServiceStatus(s.id, "approved"); }} style={{ padding: "3px 10px", borderRadius: 6, border: "none", background: "#1b7f4e", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: font }}>Reaprobar</button>
                  )}
                  {s.status === "rejected" && (
                    <button onClick={() => handleServiceStatus(s.id, "approved")} style={{ padding: "3px 10px", borderRadius: 6, border: "none", background: "#1b7f4e", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: font }}>Aprobar</button>
                  )}
                </div>
              </div>
            ))}
            {(p.services || []).length === 0 && <div style={{ textAlign: "center", padding: "16px", background: "#f7fafc", borderRadius: 8, color: "#8fa3b1", fontSize: 12 }}>Sin servicios registrados</div>}
          </div>

          {/* Documentos */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#163244", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Documentos ({(p.documents || []).length})</div>
            {(p.documents || []).length === 0 && (
              <div style={{ textAlign: "center", padding: 20, background: "#f7fafc", borderRadius: 10, border: "2px dashed #e0e9ef", color: "#8fa3b1", fontSize: 12 }}>📄 Aún no se cargaron documentos.</div>
            )}
            {(p.documents || []).map(d => {
              const docStatus = d.verification_status || d.status;
              const actions = DOC_STATUS_ACTIONS[docStatus] || [];
              return (
                <div key={d.id} style={{ borderRadius: 10, border: "1.5px solid #e0e9ef", marginBottom: 8, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", background: "#f7fafc" }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 13, color: "#163244" }}>📄 {d.document_type || d.type}</span>
                      <span style={{ fontSize: 11, color: "#8fa3b1", marginLeft: 8 }}>{d.service} · {d.created_at ? new Date(d.created_at).toLocaleDateString('es-AR') : d.uploaded_at}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {/* 27-10-2026: antes no había forma de ver el archivo y el
                          admin aprobaba documentos de identidad sin mirarlos. */}
                      {d.file_url
                        ? <a href={d.file_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 700, color: "#163244", textDecoration: "underline", whiteSpace: "nowrap" }}>Ver archivo ↗</a>
                        : <span style={{ fontSize: 11, color: "#c0392b" }}>Sin archivo</span>}
                      <Badge status={docStatus} />
                      {actions.map(a => <ActionBtn key={a.action} label={a.label} color={a.color} loading={loadingDoc === d.id + a.action} onClick={() => handleDocStatus(d.id, a.action)} />)}
                    </div>
                  </div>
                  {/\.pdf(\?|$)/i.test(d.file_url || "") && (
                    <div style={{ padding: "8px 14px", background: "#fff8e6", fontSize: 12, color: "#8a5a00" }}>
                      PDF: Cloudinary todavía no entrega PDFs (da 401) hasta que se active junto con la subida firmada. No lo apruebes sin verlo.
                    </div>
                  )}
                  {d.rejection_reason && <div style={{ padding: "8px 14px", background: "#fdecea", fontSize: 12, color: "#c0392b" }}>Motivo: {d.rejection_reason}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reject modal */}
      {rejectModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(22,50,68,0.55)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px", width: "min(420px, 90vw)", boxShadow: "0 8px 40px rgba(22,50,68,0.2)" }}>
            <h3 style={{ margin: "0 0 8px", color: "#163244", fontSize: 16, fontWeight: 800 }}>¿Confirmar rechazo?</h3>
            <p style={{ margin: "0 0 14px", color: "#5c7a8a", fontSize: 13 }}>{rejectModal.type === "profile" ? "El prestador será notificado. Podés reactivar el perfil más adelante." : "El prestador deberá volver a cargar este documento."}</p>
            <textarea placeholder="Motivo del rechazo (opcional)..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={{ ...inputStyle, width: "100%", resize: "vertical", minHeight: 80, lineHeight: 1.5, marginBottom: 14 }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setRejectModal(null); setRejectReason(""); }} style={{ padding: "10px 20px", borderRadius: 8, border: "2px solid #cddae4", background: "transparent", color: "#5c7a8a", fontFamily: font, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancelar</button>
              <button onClick={confirmReject} style={{ padding: "10px 20px", borderRadius: 8, background: "#c0392b", border: "none", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Confirmar rechazo</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Sidebar Estadísticas ─────────────────────────────────────────────────────
function SidebarStats({ providers }) {
  const stats = useMemo(() => {
    const byStatus = {}, byMonth = {};
    let identVerif = 0, phoneVerif = 0, totalRating = 0, ratingCount = 0, totalJobs = 0;
    const now = new Date();
    providers.forEach(p => {
      byStatus[p.status] = (byStatus[p.status] || 0) + 1;
      if (p.identity_verified) identVerif++;
      if (p.phone_verified)    phoneVerif++;
      if (p.rating_avg > 0)  { totalRating += parseFloat(p.rating_avg); ratingCount++; }
      totalJobs += (p.jobs_completed || 0);
      const d = new Date(p.created_at);
      const mk = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      byMonth[mk] = (byMonth[mk] || 0) + 1;
    });
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i);
      const mk = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      months.push({ label: MONTHS_ES[d.getMonth()], value: byMonth[mk] || 0 });
    }
    return { byStatus, identVerif, phoneVerif, months, totalJobs, avgRating: ratingCount > 0 ? (totalRating/ratingCount).toFixed(2) : "—", total: providers.length };
  }, [providers]);

  const maxMonth = Math.max(...stats.months.map(m => m.value), 1);
  const SCard = ({ title, children }) => (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.07)", padding: "12px 14px", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <div style={{ width: 3, height: 12, background: "#E7A432", borderRadius: 2 }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: "#163244" }}>{title}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div>
      {/* KPIs rápidos */}
      <SCard title="Métricas">
        {[
          ["⭐ Rating promedio", stats.avgRating, "#E7A432"],
          ["🔧 Trabajos totales", stats.totalJobs, "#163244"],
          ["🪪 Identidad verif.", stats.identVerif, "#1b7f4e"],
          ["📱 Teléfono verif.",  stats.phoneVerif, "#163244"],
        ].map(([l,v,c]) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid rgba(0,0,0,0.04)", fontSize: 11 }}>
            <span style={{ color: "#163244" }}>{l}</span>
            <span style={{ fontWeight: 700, color: c }}>{v}</span>
          </div>
        ))}
      </SCard>

      {/* Por estado */}
      <SCard title="Por estado">
        {Object.entries(STATUS_CONFIG).filter(([k]) => ["active","pending_review","draft","suspended","rejected"].includes(k)).map(([k, cfg]) => {
          const v = stats.byStatus[k] || 0;
          return (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid rgba(0,0,0,0.04)", fontSize: 11 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color }} />
                <span style={{ color: "#163244" }}>{cfg.label}</span>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <div style={{ width: 44, height: 4, borderRadius: 2, background: "#f0f5f8", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(v/(stats.total||1))*100}%`, background: cfg.color, borderRadius: 2 }} />
                </div>
                <span style={{ fontWeight: 700, color: cfg.color, minWidth: 18, textAlign: "right" }}>{v}</span>
              </div>
            </div>
          );
        })}
      </SCard>

      {/* Verificaciones */}
      <SCard title="Verificaciones">
        {[["🪪 Identidad", stats.identVerif, "#1b7f4e"], ["📱 Teléfono", stats.phoneVerif, "#163244"]].map(([l,v,c]) => (
          <div key={l} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#163244" }}>{l}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: c }}>{v} ({stats.total ? Math.round((v/stats.total)*100) : 0}%)</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: "#f0f5f8", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${stats.total ? (v/stats.total)*100 : 0}%`, borderRadius: 2, background: c, transition: "width 0.5s" }} />
            </div>
          </div>
        ))}
      </SCard>

      {/* Crecimiento mensual */}
      <SCard title="Registros por mes">
        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 56 }}>
          {stats.months.map((m, i) => {
            const h = maxMonth > 0 ? Math.max((m.value/maxMonth)*44, m.value>0?3:0) : 0;
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, height: "100%", justifyContent: "flex-end" }}>
                {m.value > 0 && <div style={{ fontSize: 8, color: "#7a93a4", fontWeight: 600 }}>{m.value}</div>}
                <div style={{ width: "100%", borderRadius: "2px 2px 0 0", background: "#1b7f4e", height: `${h}px`, opacity: 0.5 + (i/5)*0.5 }} />
                <div style={{ fontSize: 8, color: "#aab8c2", fontWeight: 600 }}>{m.label}</div>
              </div>
            );
          })}
        </div>
      </SCard>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminProvidersPage() {
  const [providers,    setProviders]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadingError, setLoadingError] = useState("");
  const [search,       setSearch]       = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selected,     setSelected]     = useState(null);
  const [page,         setPage]         = useState(0);

  const fetchProviders = useCallback(async () => {
    setLoading(true); setLoadingError("");
    try {
      const res = await api.get("/providers/admin-op/list");
      setProviders(res.data.providers || res.data || []);
    } catch (err) {
      console.error(err);
      setLoadingError("No se pudo cargar la lista de prestadores.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProviders(); }, [fetchProviders]);

  const STATUS_FILTERS = [
    { value: "all",            label: "Todos"       },
    { value: "active",         label: "Activos"     },
    { value: "pending_review", label: "En revisión" },
    { value: "draft",          label: "Borrador"    },
    { value: "suspended",      label: "Suspendidos" },
    { value: "rejected",       label: "Rechazados"  },
  ];

  const filtered = useMemo(() => providers.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.display_name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q) || p.dni?.includes(q);
    return matchSearch && (filterStatus === "all" || p.status === filterStatus);
  }), [providers, search, filterStatus]);

  const paginated  = filtered.slice(page * PAGE_SIZE, (page+1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const handleUpdate = async (updated) => {
    setProviders(ps => ps.map(p => p.id === updated.id ? updated : p));
    try {
      const res = await api.get(`/providers/admin-op/${updated.id}`);
      const fresh = res.data.provider || res.data;
      setProviders(ps => ps.map(p => p.id === fresh.id ? fresh : p));
      setSelected({ documents: [], services: [], ...fresh });
    } catch { setSelected({ documents: [], services: [], ...updated }); }
  };

  const handleSelect = async (provider) => {
    setSelected({ documents: [], services: [], ...provider });
    try {
      const res = await api.get(`/providers/admin-op/${provider.id}`);
      setSelected({ documents: [], services: [], ...(res.data.provider || res.data) });
    } catch (err) { console.error(err); }
  };

  // Stats para KPIs
  const kpis = useMemo(() => ({
    total:    providers.length,
    active:   providers.filter(p => p.status === "active").length,
    review:   providers.filter(p => p.status === "pending_review").length,
    suspended:providers.filter(p => p.status === "suspended").length,
    draft:    providers.filter(p => p.status === "draft").length,
  }), [providers]);

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "22px 20px 50px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 260px", gap: 16, alignItems: "start" }}>

          {/* ── COLUMNA PRINCIPAL ───────────────────────────────────── */}
          <div>
            <div style={{ marginBottom: 14 }}>
              <h1 style={{ margin: "0 0 3px", fontSize: 19, fontWeight: 800, color: "#163244" }}>Prestadores</h1>
              <p style={{ margin: 0, color: "#7a93a4", fontSize: 12 }}>Gestión de perfiles, documentos y aprobaciones.</p>
            </div>

            {/* KPIs */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 12 }}>
              {[
                { label:"Total",      val: kpis.total,    col:"#163244", icon:"🔧" },
                { label:"Activos",    val: kpis.active,   col:"#1b7f4e", icon:"✓"  },
                { label:"En revisión",val: kpis.review,   col:"#b06a00", icon:"⏳" },
                { label:"Borrador",   val: kpis.draft,    col:"#5c7a8a", icon:"📝" },
                { label:"Suspendidos",val: kpis.suspended,col:"#c0392b", icon:"⚠"  },
              ].map(s => (
                <div key={s.label} style={{ background:"#fff", borderRadius:10, border:"1px solid rgba(0,0,0,0.07)", padding:"10px 12px" }}>
                  <div style={{ fontSize:9, fontWeight:700, color:"#7a93a4", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>{s.icon} {s.label}</div>
                  <div style={{ fontSize:20, fontWeight:900, color:s.col, lineHeight:1 }}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* Filtros */}
            <div style={{ display:"flex", gap:8, marginBottom:10, alignItems:"center", flexWrap:"wrap" }}>
              <input style={{ padding:"7px 10px", borderRadius:8, border:"1.5px solid rgba(0,0,0,0.12)", fontSize:12, fontFamily:font, color:"#163244", background:"#fff", outline:"none", flex:"1 1 200px", minWidth:0 }} placeholder="🔍  Nombre, email o DNI..." value={search} onChange={e=>{ setSearch(e.target.value); setPage(0); }} />
              <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                {STATUS_FILTERS.map(f => (
                  <button key={f.value} onClick={()=>{ setFilterStatus(f.value); setPage(0); }} style={{ padding:"5px 11px", borderRadius:20, border:`1.5px solid ${filterStatus===f.value?"#163244":"rgba(0,0,0,0.1)"}`, background:filterStatus===f.value?"#163244":"#fff", color:filterStatus===f.value?"#fff":"#5a6e7f", fontFamily:font, fontWeight:600, fontSize:11, cursor:"pointer" }}>{f.label}</button>
                ))}
              </div>
              <button onClick={fetchProviders} style={{ marginLeft:"auto", padding:"6px 14px", borderRadius:50, background:"#163244", color:"#fff", border:"none", fontFamily:font, fontWeight:700, fontSize:11, cursor:"pointer" }}>↻ Actualizar</button>
            </div>

            {loadingError && <div style={{ background:"rgba(192,57,43,0.07)", border:"1px solid rgba(192,57,43,0.2)", borderRadius:8, padding:"9px 12px", fontSize:12, color:"#c0392b", marginBottom:10 }}>⚠ {loadingError}</div>}

            {/* Tabla */}
            <div style={{ background:"#fff", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1.8fr 1.4fr 0.8fr 0.8fr 0.7fr 0.7fr 0.8fr 55px", padding:"9px 14px", background:"#163244", gap:6, alignItems:"center" }}>
                {["Prestador","Email / DNI","Estado","Servicios","Docs","Verif.","Rating",""].map((h,i) => (
                  <div key={i} style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.55)", textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</div>
                ))}
              </div>

              {loading && <div style={{ textAlign:"center", padding:"32px", color:"#7a93a4", fontSize:12 }}><span style={{ display:"inline-block", width:20, height:20, border:"2px solid #dce5ec", borderTopColor:"#163244", borderRadius:"50%", animation:"spin 0.7s linear infinite", marginBottom:8 }} /><br/>Cargando…</div>}

              {!loading && paginated.map((p, i) => {
                const pendingDocs = (p.documents || []).filter(d => (d.verification_status || d.status) === "pending").length;
                return (
                  <div key={p.id}
                    style={{ display:"grid", gridTemplateColumns:"1.8fr 1.4fr 0.8fr 0.8fr 0.7fr 0.7fr 0.8fr 55px", padding:"10px 14px", gap:6, alignItems:"center", borderBottom:i<paginated.length-1?"1px solid rgba(0,0,0,0.04)":"none", transition:"background 0.12s" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#fafcfe"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  >
                    {/* Prestador */}
                    <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
                      {p.profile_photo_url
                        ? <img src={p.profile_photo_url} alt="" style={{ width:28, height:28, borderRadius:"50%", objectFit:"cover", flexShrink:0 }} />
                        : <div style={{ width:28, height:28, borderRadius:"50%", background:"#163244", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:"#E7A432", flexShrink:0 }}>{p.display_name[0]}</div>
                      }
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:11, color:"#163244", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.display_name}</div>
                        {p.created_at && <div style={{ fontSize:9, color:"#aab8c2" }}>{new Date(p.created_at).toLocaleDateString("es-AR")}</div>}
                      </div>
                    </div>
                    {/* Email/DNI */}
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:10, color:"#5a6e7f", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.email}</div>
                      <div style={{ fontSize:9, color:"#aab8c2" }}>DNI: {p.dni || "—"}</div>
                    </div>
                    {/* Estado */}
                    <div><Badge status={p.status} /></div>
                    {/* Servicios */}
                    <div style={{ display:"flex", flexWrap:"wrap", gap:3 }}>
                      {(p.services || []).slice(0,2).map(s => (
                        <span key={s.id} style={{ fontSize:9, padding:"1px 6px", borderRadius:10, background:"#f0f5f8", color:"#163244", fontWeight:600, whiteSpace:"nowrap" }}>{s.service_name || s.label}</span>
                      ))}
                      {(p.services || []).length > 2 && <span style={{ fontSize:9, color:"#aab8c2" }}>+{(p.services||[]).length-2}</span>}
                    </div>
                    {/* Docs */}
                    <div>
                      {(p.documents||[]).length === 0
                        ? <span style={{ fontSize:10, color:"#aab8c2" }}>—</span>
                        : <div style={{ fontSize:10, color:"#163244", fontWeight:600 }}>
                            {(p.documents||[]).length}
                            {pendingDocs > 0 && <span style={{ marginLeft:4, fontSize:9, padding:"1px 5px", borderRadius:8, background:"#fff3e0", color:"#b06a00", fontWeight:700 }}>{pendingDocs}⚠</span>}
                          </div>
                      }
                    </div>
                    {/* Verif */}
                    <div style={{ fontSize:12 }}>
                      {p.identity_verified ? "🪪✓" : "🪪✕"}
                    </div>
                    {/* Rating */}
                    <div style={{ fontSize:11, fontWeight:700, color:"#163244" }}>
                      {p.rating_avg ? <span style={{ color:"#E7A432" }}>⭐{parseFloat(p.rating_avg).toFixed(1)}</span> : <span style={{ color:"#aab8c2" }}>—</span>}
                      {p.jobs_completed > 0 && <div style={{ fontSize:9, color:"#aab8c2" }}>{p.jobs_completed} trab.</div>}
                    </div>
                    {/* Acción */}
                    <button onClick={()=>handleSelect(p)} style={{ padding:"4px 10px", borderRadius:50, background:"#163244", border:"none", color:"#fff", fontFamily:font, fontWeight:600, fontSize:10, cursor:"pointer" }}>Ver →</button>
                  </div>
                );
              })}

              {!loading && paginated.length === 0 && <div style={{ textAlign:"center", padding:"32px", color:"#aab8c2", fontSize:12 }}>No se encontraron prestadores</div>}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10 }}>
                <div style={{ fontSize:11, color:"#7a93a4" }}>{filtered.length} prestador{filtered.length!==1?"es":""} · pág. {page+1} de {totalPages}</div>
                <div style={{ display:"flex", gap:3 }}>
                  <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(0,0,0,0.1)", background:"#fff", color:page===0?"#ccc":"#163244", fontFamily:font, fontWeight:600, fontSize:11, cursor:page===0?"not-allowed":"pointer" }}>← Ant.</button>
                  {Array.from({length:Math.min(totalPages,7)},(_,i)=>{ const pg=totalPages<=7?i:Math.max(0,Math.min(page-3,totalPages-7))+i; return <button key={pg} onClick={()=>setPage(pg)} style={{ width:28, height:28, borderRadius:7, border:`1px solid ${pg===page?"#163244":"rgba(0,0,0,0.1)"}`, background:pg===page?"#163244":"#fff", color:pg===page?"#fff":"#163244", fontFamily:font, fontWeight:700, fontSize:11, cursor:"pointer" }}>{pg+1}</button>; })}
                  <button onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page===totalPages-1} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(0,0,0,0.1)", background:"#fff", color:page===totalPages-1?"#ccc":"#163244", fontFamily:font, fontWeight:600, fontSize:11, cursor:page===totalPages-1?"not-allowed":"pointer" }}>Sig. →</button>
                </div>
              </div>
            )}
          </div>

          {/* ── SIDEBAR ─────────────────────────────────────────────── */}
          <SidebarStats providers={providers} />
        </div>
      </div>

      {selected && <ProviderDrawer provider={selected} onClose={()=>setSelected(null)} onUpdate={handleUpdate} />}

      <style>{`
        * { box-sizing: border-box; }
        input:focus, select:focus, textarea:focus { border-color: #163244 !important; outline: none; box-shadow: 0 0 0 3px rgba(22,50,68,0.08); }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
